//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, RoleEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { APR, otAssert } from '@shared/conversions'

import { PoolAbi, PoolControllerAbi } from '@shared/Abis'
import { AlertCTO } from '@shared/AlertCTO'

import { dateToEthDate, EthAddr, ProviderWritable, TxHash } from '@shared/SchemaTypes'

import { alertActions } from '@react/redux/alertSlice'
import { poolActions } from '@react/redux/poolSlice'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'
import { UserConnection } from '@shared/DeploymentConfigs'

export interface CreateLoanOpts {
  poolAddr: EthAddr
  accrualStartDayTimestamp: number
  transferInWindowDurationDays: number
  transferOutWindowDurationDays: number
  indicativeApr: APR
  finalizedApr: APR
  tenor: number
  originationBps: number
  priorLoanAddr: EthAddr
}

export const loanApproveTx =
  (props: { poolAddr: EthAddr; loanAddr: EthAddr }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.LOAN_APPROVE

    try {
      logOT.debug('approveLoanSaga start')
      const { poolAddr, loanAddr } = props
      const { webSocket } = getState().chainState
      const { chainConfigurationName, perimeterAddresses } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      otAssert(perimeterAddresses, 'perimeterAddresses is not defined')
      await loanApprove(perimeterAddresses, webSocket, signer as Signer, poolAddr, loanAddr, chainConfigurationName)
      dispatch(poolActions.approveLoanSuccess({ poolAddr, loanAddr }))
      logOT.debug('approveLoanSaga complete')
      const successAlert: AlertCTO = {
        sourceObjectAddr: loanAddr,
        title: 'Loan approved.',
        description: '',
        createdAt: dateToEthDate(new Date()),
        dueDateTime: 0,
        alertLevel: AlertLevelEnum.SUCCESS,
      }
      dispatch(alertActions.addAlert(successAlert))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 4001)
    }
  }

export const loanApprove = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  loanAddr: EthAddr,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('loanApprove poolConfigSettings', perimeterAddresses.PoolFactory, !!poolAdminSigner)

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.LOAN_APPROVE, poolAddr, uuid, chainConfigurationName))
  const providerWritable = poolAdminSigner.provider as ProviderWritable
  const Pool = new Contract(poolAddr, PoolAbi, providerWritable)
  const poolControllerAddr = await Pool.poolController()
  otAssert(poolControllerAddr)
  const poolControllerContract = new Contract(poolControllerAddr, PoolControllerAbi, poolAdminSigner)

  const createdPoolTx = await poolControllerContract.approveLoanForPool(loanAddr)

  webSocket?.send(
    wsServerActionPoolTX(
      ActivityEnum.POOL_ACTIVATE,
      poolAddr,
      uuid,
      createdPoolTx.hash as TxHash,
      chainConfigurationName
    )
  )

  await createdPoolTx.wait(1)

  logOT.debug('loanApprove Success')
}
